import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import CheckCircleIcon from '@material-ui/icons/CheckCircle';

import { StatusChip } from '@Components';
import { Button, ButtonProps } from '@Components/common';
import { useAppLayoutContext, useUserContext } from '@Contexts';
import {
  SelectionStatusEnum,
  UserTypeEnum,
  FundStatusEnum,
  ErrorCode,
  FundGivingEngine,
} from '@Types';
import { useRouter } from '@Helpers/useRouter';
import { isMatchmakingApplyFlow } from '@Helpers/isMatchmakingApply';

import styles from './CtaButton.module.scss';
import { useCreateMatchmakingApplyFundNonProfitLink } from '@Hooks/Api/useCreateMatchmakingApplyFundNonProfitLink';
import * as Sentry from '@sentry/react';
import { Routes, RoutingPaths } from '@App/paths';

enum CTA_STATES_ENUM {
  FUND_FUNDER_ADMIN = 'FUND_FUNDER_ADMIN',
  FUND_FUNDER_NONADMIN = 'FUND_FUNDER_NONADMIN',
  FUND_GROUP_MATCHED = 'FUND_GROUP_MATCHED',
  FUND_GROUP_UNMATCHED = 'FUND_GROUP_UNMATCHED',
  FUND_UNLOGGED_USER = 'FUND_UNLOGGED_USER',
}

interface CtaButtonProps {
  headerRef: React.MutableRefObject<React.ReactNode>;
  fund: boolean;
  fundId: number;
  userCanManage: boolean;
  selectionId?: number;
  selectionStatus?: SelectionStatusEnum;
  status?: string;
  groupHasProjectMatched?: boolean;
  onApplyClick?: () => void;
  hasEditableLiveProjects?: boolean;
  fundGivingEngine?: FundGivingEngine;
}

export const CtaButton: React.FC<CtaButtonProps> = ({
  headerRef,
  fund,
  fundId,
  userCanManage,
  selectionId,
  selectionStatus,
  status,
  groupHasProjectMatched,
  onApplyClick,
  hasEditableLiveProjects = false,
  fundGivingEngine,
}) => {
  const [isBtnInView, setIsBtnInView] = useState(true);
  const [ctaState, setCtaState] = useState<CTA_STATES_ENUM>();
  const { setHeaderButtons } = useAppLayoutContext();
  const { t: tFund } = useTranslation('fund');
  const { userData } = useUserContext();
  const { push } = useRouter();

  const [createMatchmakingApplyFundNonProfitLink] = useCreateMatchmakingApplyFundNonProfitLink();

  const isBtnPrimary = ctaState !== CTA_STATES_ENUM.FUND_FUNDER_ADMIN;

  const handleScroll = useCallback(() => {
    const { bottom } = (headerRef.current as HTMLElement)?.getBoundingClientRect() || {};

    if (!bottom) return;

    if (bottom < 80 && isBtnInView) {
      setIsBtnInView(false);
    } else if (bottom >= 80 && !isBtnInView) {
      setIsBtnInView(true);
    }
  }, [headerRef, isBtnInView]);

  useEffect(() => {
    if (window) {
      window.addEventListener('scroll', handleScroll, { passive: true });

      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  useEffect(() => {
    if (userData) {
      if (userData.user_type === UserTypeEnum.FUNDER) {
        if (userCanManage) {
          setCtaState(CTA_STATES_ENUM.FUND_FUNDER_ADMIN);
        } else {
          setCtaState(CTA_STATES_ENUM.FUND_FUNDER_NONADMIN);
        }
      } else {
        if (groupHasProjectMatched) {
          setCtaState(CTA_STATES_ENUM.FUND_GROUP_MATCHED);
        } else if (status === tFund(`fund_status.${FundStatusEnum.LIVE}`)) {
          setCtaState(CTA_STATES_ENUM.FUND_GROUP_UNMATCHED);
        }
      }
    } else if (status === tFund(`fund_status.${FundStatusEnum.LIVE}`)) {
      setCtaState(CTA_STATES_ENUM.FUND_UNLOGGED_USER);
    }
  }, [
    fund,
    userCanManage,
    groupHasProjectMatched,
    userData,
    selectionId,
    selectionStatus,
    status,
    tFund,
  ]);

  const mapCtaAttributes = useMemo(
    () => ({
      [CTA_STATES_ENUM.FUND_FUNDER_ADMIN]: {
        ctaText: tFund('details_page.manage_fund'),
        onClick: () => {
          push(`/fund/${fundId}/manage`);
        },
      },
      [CTA_STATES_ENUM.FUND_GROUP_UNMATCHED]: {
        ctaText: hasEditableLiveProjects
          ? tFund('details_page.apply_now')
          : tFund('details_page.create_project_to_apply'),
        onClick: () => {
          if (isMatchmakingApplyFlow(fundGivingEngine?.matchmaking.part)) {
            if (hasEditableLiveProjects) {
              onApplyClick?.();
              return;
            }

            createMatchmakingApplyFundNonProfitLink(fundId)
              .then(() => push(Routes.PROJECT_CREATE))
              .catch((error) => {
                if (error.status === 409 && error.data?.error === ErrorCode.LINK_ALREADY_EXISTS) {
                  // Link already exists - that's fine, proceeding anyway
                  push(Routes.PROJECT_CREATE);
                } else {
                  // Log unexpected errors to Sentry
                  Sentry.captureException(error);
                }
              });
          } else {
            push(Routes.PROJECT_CREATE);
          }
        },
      },
      [CTA_STATES_ENUM.FUND_UNLOGGED_USER]: {
        ctaText: tFund('details_page.apply_now'),
        onClick: () => {
          if (isMatchmakingApplyFlow(fundGivingEngine?.matchmaking.part)) {
            push(Routes.SIGN_UP_GROUP_WITH_APPLY_FUND(fundId));
          } else {
            push(RoutingPaths.SIGNUP_GROUP);
          }
        },
      },
    }),
    [
      fundId,
      push,
      tFund,
      createMatchmakingApplyFundNonProfitLink,
      fundGivingEngine,
      hasEditableLiveProjects,
      onApplyClick,
    ],
  );

  const ButtonWithProps = useCallback(
    ({ className, ...props }: ButtonProps) => {
      if (
        ctaState &&
        ctaState !== CTA_STATES_ENUM.FUND_FUNDER_NONADMIN &&
        ctaState !== CTA_STATES_ENUM.FUND_GROUP_MATCHED
      ) {
        const ctaAttributes: { ctaText: string; onClick: () => void; startIcon?: JSX.Element } =
          ctaState && mapCtaAttributes[ctaState];

        return (
          <Button
            className={className}
            onClick={ctaAttributes.onClick}
            startIcon={ctaAttributes.startIcon}
            aria-label={fund ? 'fund-page-cta-button' : 'project-page-cta-button'}
            {...props}
          >
            {ctaAttributes.ctaText}
          </Button>
        );
      }

      return null;
    },
    [ctaState, mapCtaAttributes, fund],
  );

  useEffect(() => {
    setHeaderButtons(
      isBtnInView ? undefined : (
        <ButtonWithProps buttonType={isBtnPrimary ? 'primary' : 'tertiary'} />
      ),
    );

    return () => {
      setHeaderButtons(undefined);
    };
  }, [setHeaderButtons, ButtonWithProps, isBtnInView, isBtnPrimary]);

  return (
    <>
      <ButtonWithProps buttonType={isBtnPrimary ? 'primary' : 'tertiary'} />
      {ctaState === CTA_STATES_ENUM.FUND_GROUP_MATCHED && (
        <StatusChip
          color="primary"
          label={
            <span className={styles.chipLabel}>
              <CheckCircleIcon style={{ fontSize: '22px' }} />
              <span>{tFund('details_page.matched')}</span>
            </span>
          }
        />
      )}
    </>
  );
};
